/**
 * Leitura dos dados de projeto da linha a partir do cadastro real de
 * equipamentos/tubulação — é isto que elimina a redigitação na calculadora
 * ASME B31.3.
 *
 * Tudo sai normalizado nas unidades da fórmula (MPa, °C, mm) e cada campo
 * carrega a sua ORIGEM, para a tela distinguir "veio do cadastro" de "veio da
 * tabela dimensional" e de "informado no cenário de cálculo".
 */

import { findPipeDimensionByDn, findPipeDimensionByNps, PIPE_DIMENSION_SOURCE } from "./pipe-dimension-catalog.js";

export type FieldOrigin = "REGISTRY" | "DIMENSION_CATALOG" | "NOT_REGISTERED";

export interface ResolvedField<T> {
  value: T | null;
  origin: FieldOrigin;
  /** Texto exibido ao lado do valor ("Cadastro da linha", "Tabela dimensional..."). */
  sourceLabel: string;
}

export interface LineDesignData {
  equipmentId: string;
  clientId: string;
  projectId: string | null;
  unitAreaId: string | null;
  tag: string;
  equipmentType: string;

  service: string | null;
  fluid: string | null;
  fluidClass: string | null;
  pressureClass: string | null;

  nps: string | null;
  dn: number | null;
  schedule: string | null;
  outsideDiameterMm: ResolvedField<number>;
  nominalThicknessMm: ResolvedField<number>;

  material: string | null;
  materialSpecification: string | null;
  materialGrade: string | null;

  designPressureMPa: ResolvedField<number>;
  designTemperatureCelsius: ResolvedField<number>;
  corrosionAllowanceMm: ResolvedField<number>;

  fabricationStandard: string | null;
  inServiceInspectionCode: string | null;
}

const REGISTRY_LABEL = "Cadastro da linha";
const NOT_REGISTERED_LABEL = "Não cadastrado";

const notRegistered = <T>(): ResolvedField<T> => ({ value: null, origin: "NOT_REGISTERED", sourceLabel: NOT_REGISTERED_LABEL });
const fromRegistry = <T>(value: T): ResolvedField<T> => ({ value, origin: "REGISTRY", sourceLabel: REGISTRY_LABEL });

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const parsed = Number(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

/** Aceita number, string ou MeasurementValue ({ value, unit }). */
function readMeasurement(raw: unknown): { value: number | null; unit: string } {
  if (raw && typeof raw === "object" && "value" in (raw as any)) {
    const measurement = raw as { value: unknown; unit?: unknown };
    return { value: toNumber(measurement.value), unit: String(measurement.unit ?? "").trim() };
  }
  return { value: toNumber(raw), unit: "" };
}

/** Converte para MPa. Unidade desconhecida não é "assumida" como MPa: devolve null. */
export function toMegaPascal(value: number | null, unit: string): number | null {
  if (value === null) return null;
  const u = unit.toLowerCase().replace(/\s/g, "");
  if (!u || u === "mpa") return value;
  if (u === "bar" || u === "barg" || u === "bara") return value / 10;
  if (u === "kpa") return value / 1000;
  if (u === "pa") return value / 1e6;
  if (u === "psi" || u === "psig" || u === "psia" || u === "lbf/in²" || u === "lbf/in2") return value * 0.00689475729;
  if (u === "kgf/cm²" || u === "kgf/cm2" || u === "kg/cm²" || u === "kg/cm2") return value * 0.0980665;
  return null;
}

/** Converte para °C. Unidade vazia é tratada como °C (padrão do cadastro brasileiro). */
export function toCelsius(value: number | null, unit: string): number | null {
  if (value === null) return null;
  const u = unit.toLowerCase().replace(/\s|°/g, "");
  if (!u || u === "c" || u === "ºc") return value;
  if (u === "f" || u === "ºf") return (value - 32) * (5 / 9);
  if (u === "k") return value - 273.15;
  return null;
}

/** Converte para mm. Unidade vazia é tratada como mm. */
export function toMillimeters(value: number | null, unit: string): number | null {
  if (value === null) return null;
  const u = unit.toLowerCase().replace(/\s/g, "");
  if (!u || u === "mm") return value;
  if (u === "cm") return value * 10;
  if (u === "m") return value * 1000;
  if (u === "in" || u === '"' || u === "pol") return value * 25.4;
  return null;
}

/**
 * Lê um equipamento/linha do cadastro e devolve os dados de projeto prontos
 * para a calculadora. Nada é inventado: campo ausente vira NOT_REGISTERED e a
 * tela mostra isso ao usuário.
 */
export function extractLineDesignData(equipment: any): LineDesignData {
  const pressure = readMeasurement(equipment?.designPressure);
  const temperature = readMeasurement(equipment?.designTemperature);
  const corrosion = readMeasurement(equipment?.corrosionAllowance);
  const nominal = readMeasurement(equipment?.nominalThickness);
  const outside = readMeasurement(equipment?.outsideDiameter);

  const nps = equipment?.nps ? String(equipment.nps) : null;
  const dimensionRow = findPipeDimensionByNps(nps) ?? findPipeDimensionByDn(equipment?.nominalDiameter);

  const outsideMm = toMillimeters(outside.value, outside.unit);
  let outsideDiameter: ResolvedField<number>;
  if (outsideMm !== null) {
    outsideDiameter = fromRegistry(outsideMm);
  } else if (dimensionRow) {
    // A linha não tem OD cadastrado: o valor vem da tabela dimensional pelo
    // NPS/DN — nunca do próprio número do DN.
    outsideDiameter = {
      value: dimensionRow.outsideDiameterMm,
      origin: "DIMENSION_CATALOG",
      sourceLabel: `${PIPE_DIMENSION_SOURCE} (NPS ${dimensionRow.nps}")`
    };
  } else {
    outsideDiameter = notRegistered<number>();
  }

  const pressureMPa = toMegaPascal(pressure.value, pressure.unit);
  const temperatureC = toCelsius(temperature.value, temperature.unit);
  const corrosionMm = toMillimeters(corrosion.value, corrosion.unit);
  const nominalMm = toMillimeters(nominal.value, nominal.unit);

  return {
    equipmentId: String(equipment?.id ?? ""),
    clientId: String(equipment?.clientId ?? ""),
    projectId: equipment?.projectId ?? null,
    unitAreaId: equipment?.unitAreaId ?? null,
    tag: String(equipment?.tag ?? ""),
    equipmentType: String(equipment?.equipmentType ?? ""),

    service: equipment?.service ?? null,
    fluid: equipment?.fluid ?? null,
    fluidClass: equipment?.fluidClass ?? null,
    pressureClass: equipment?.pressureClass ?? null,

    nps: nps ?? (dimensionRow ? dimensionRow.nps : null),
    dn: dimensionRow ? dimensionRow.dn : toNumber(equipment?.nominalDiameter),
    schedule: equipment?.schedule ?? null,
    outsideDiameterMm: outsideDiameter,
    nominalThicknessMm: nominalMm !== null ? fromRegistry(nominalMm) : notRegistered<number>(),

    material: equipment?.material ?? null,
    materialSpecification: equipment?.materialSpecification ?? null,
    materialGrade: equipment?.materialGrade ?? null,

    designPressureMPa: pressureMPa !== null ? fromRegistry(pressureMPa) : notRegistered<number>(),
    designTemperatureCelsius: temperatureC !== null ? fromRegistry(temperatureC) : notRegistered<number>(),
    corrosionAllowanceMm: corrosionMm !== null ? fromRegistry(corrosionMm) : notRegistered<number>(),

    fabricationStandard: equipment?.fabricationStandard ?? null,
    inServiceInspectionCode: equipment?.inServiceInspectionCode ?? null
  };
}

/**
 * Compara o valor cadastrado com o que será usado no cálculo. A calculadora
 * pode rodar um cenário diferente do cadastro, mas isso precisa ficar visível
 * — e nunca reescreve o cadastro mestre.
 */
export interface ScenarioComparison {
  registered: number | null;
  used: number | null;
  isScenario: boolean;
}

export function compareScenario(registered: number | null, used: number | null, tolerance = 1e-9): ScenarioComparison {
  const isScenario =
    used !== null &&
    registered !== null &&
    Math.abs(used - registered) > tolerance;
  return { registered, used, isScenario };
}
